import InputAdornment from '@mui/material/InputAdornment';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Dropdown, BhiFormDialog, TextInput } from 'src/UIKit';
import { useSelector } from 'react-redux';
import { useInviteUserApi, useUpdateUserRoleApi } from 'src/api/users';
import { User } from 'src/constants/types';
import {
  inviteUserValidationSchema,
  ValidationErrors,
} from 'src/constants/validations';
import { filterAllowedRoles, reorderRoles } from 'src/helpers/permissions';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { selectProfile, selectRoles } from 'src/modules/profile/slices';
import * as Yup from 'yup';

type InviteUserModalType = {
  show: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  userData?: User;
  refresh: () => void;
};

const initialValues = { email: "", firstName: "", lastName: "", phoneNumber: "", roleId: "" };

export default function InviteUserModal({ show, setShowModal, refresh, userData }: InviteUserModalType) {
  const [formValues, setFormValues] = useState(initialValues);
  const [errors, setErrors] = useState<ValidationErrors>({});

  const selectedCompanyId: string | undefined = useSelector(getCurrentCompanyId);
  const { profile } = useSelector(selectProfile);
  const { roles } = useSelector(selectRoles);
  const allowedRoles = useMemo(
    () => filterAllowedRoles(profile?.role ? profile.role.name : "bhi_admin", roles),
    [roles, profile]
  );
  const inviteUser = useInviteUserApi();
  const updateRole = useUpdateUserRoleApi();

  const handleClose = () => {
    setFormValues(initialValues);
    setErrors({});
    setShowModal(false);
    refresh();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const validate = async (values: typeof formValues) => {
    try {
      await inviteUserValidationSchema.validate(values, { abortEarly: false });
      setErrors({});
      return true;
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const newErrors = err.inner?.reduce((acc, curr) => {
          if (curr.path) {
            acc[curr.path] = curr?.message;
          }
          return acc;
        }, {} as ValidationErrors);
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleInviteUser = async () => {
    const isValid = await validate(formValues);
    if (isValid) {
      selectedCompanyId &&
        inviteUser([
          {
            ...formValues,
            phoneNumber: formValues.phoneNumber ? "+1" + formValues.phoneNumber : "",
          }],
          selectedCompanyId
        )
          .then(() => {
            handleClose();
            setErrors({});
            refresh();
          })
          .catch((e) => {
            if (e.error === "conflict") {
              setErrors({ validation: "User with that email already exists!" });
            }
          });
    }
  };

  const handleUserEditRole = () => {
    selectedCompanyId &&
      userData?.id &&
      updateRole({ roleId: formValues.roleId }, userData?.id, selectedCompanyId)
        .then(() => {
          handleClose();
          refresh();
          setErrors({});
        })
        .catch((e) => {
          if (e.error === "conflict" || e.error === "validation") {
            setErrors({ validation: "Something went wrong!" });
          }
        });
  };

  useEffect(() => {
    userData &&
      setFormValues({
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
        phoneNumber: userData.phoneNumber,
        roleId: userData.role.id,
      });
  }, [userData?.id]);

  return (
    <BhiFormDialog
      onClose={handleClose}
      open={show}
      title={userData ? 'Edit User' : 'Invite User'}
      error={errors.validation}
      actions={
        <>
          <Button variant="outlined" onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={userData ? handleUserEditRole : handleInviteUser}>
            {userData ? 'Save' : 'Invite'}
          </Button>
        </>
      }
    >
      <TextInput
        label="First Name"
        name="firstName"
        margin="none"
        size="small"
        value={formValues.firstName}
        onChange={handleChange}
        disabled={!!userData}
        error={!!errors.firstName}
        helperText={errors.firstName}
      />
      <TextInput
        label="Last Name"
        name="lastName"
        margin="none"
        size="small"
        value={formValues.lastName}
        onChange={handleChange}
        disabled={!!userData}
        error={!!errors.lastName}
        helperText={errors.lastName}
      />
      <TextInput
        label="Email"
        name="email"
        margin="none"
        size="small"
        value={formValues.email}
        onChange={handleChange}
        disabled={!!userData}
        error={!!errors.email}
        helperText={errors.email}
      />
      <TextInput
        label="Phone Number"
        name="phoneNumber"
        type="tel"
        margin="none"
        size="small"
        InputProps={{ startAdornment: <InputAdornment position="start">+1</InputAdornment> }}
        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
        value={formValues.phoneNumber?.replace('+1', '')}
        onChange={handleChange}
        disabled={!!userData}
        error={!!errors.phoneNumber}
        helperText={errors.phoneNumber}
      />
      <Dropdown
        label="Role"
        margin="none"
        size="small"
        value={formValues.roleId}
        options={reorderRoles(roles).map(role => ({
          value: role.id,
          title: role.title,
          disabled: !allowedRoles.includes(role.id),
        }))}
        onChange={(e, roleId) => setFormValues({ ...formValues, roleId })}
        error={!!errors.roleId}
        helperText={errors.roleId}
      />
    </BhiFormDialog>
  );
}
